import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AnalysisStepsState, PpiAnalysisState, initialPpiAnalysisState, analysisSteps as stepDefinitions } from '../../../types';
import { getFirebaseAuth } from '../../../services/firebase';
import { authPpiApiAxiosWithRefresh } from '../../../services/api';
import { PPI_API } from '../../../services/constants';
import { SimilarCar } from '../../../types';

export const PpiAnalysisContext = createContext<PpiAnalysisState>(initialPpiAnalysisState);

export const usePpiAnalysis = () => useContext(PpiAnalysisContext);

interface PpiAnalysisProviderProps {
  children: ReactNode;
  docId: string | null;
}

const getInitialStepsState = (): AnalysisStepsState => {
  const steps: AnalysisStepsState = {};
  stepDefinitions.forEach(step => {
    steps[step.key] = { status: 'pending' };
  });
  return steps;
};

export const PpiAnalysisProvider: React.FC<PpiAnalysisProviderProps> = ({ children, docId }) => {
  const [analysisStarted, setAnalysisStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisSteps, setAnalysisSteps] = useState<AnalysisStepsState>(getInitialStepsState());
  const [isComplete, setIsComplete] = useState(false);
  const [sentEmailAddress, setSentEmailAddress] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [similarCars, setSimilarCars] = useState<SimilarCar[]>([]);

  const resetState = useCallback(() => {
    setAnalysisStarted(false);
    setLoading(false);
    setError(null);
    setAnalysisSteps(getInitialStepsState());
    setIsComplete(false);
    setSentEmailAddress(null);
    setEmailError(null);
    setSimilarCars([]);
  }, []);

  const fetchSimilarCars = useCallback(async () => {
    if (docId) { // Check if docId is available
      try {
        const similarCarsResponse = await (await authPpiApiAxiosWithRefresh()).get(`/getSimilarSold/${docId}`);
        setSimilarCars(similarCarsResponse.data);
      } catch (err) {
        console.error("Failed to fetch similar cars:", err);
      }
    }
  }, [docId]);

  useEffect(() => {
    const loadExistingAnalysis = async () => {
      if (!docId) return;

      setLoading(true);
      try {
        const response = await (await authPpiApiAxiosWithRefresh()).get(`/get-analysis-data/${docId}`);
        const data = await response.data;

        if (data.success) {
          setAnalysisSteps(data.analysisSteps);
          setSentEmailAddress(data.email || null);

          // Fetch similar cars as soon as vehicle details are available from loaded analysis
          fetchSimilarCars();

          if (data.overallStatus === 'completed' || data.overallStatus === 'error') {
            setIsComplete(true);
            setAnalysisStarted(false);
          } else if (data.overallStatus === 'in_progress') {
            setAnalysisStarted(true);
            setIsComplete(false);
            // The original call to fetchSimilarCars here is now handled above in a more general way.
            // Removed: fetchSimilarCars(data.vehicleDetails);
            // Polling removed. This will just show the last known state.
          } else {
            const hasStarted = Object.values(data.analysisSteps).some((s: any) => s.status !== 'pending');
            setAnalysisStarted(hasStarted);
            setIsComplete(false);
          }

        } else {
          setError(data.error || 'Failed to load existing analysis.');
        }
      } catch (err) {
        setError('Error loading existing analysis.');
        console.error('Error loading existing analysis:', err);
      } finally {
        setLoading(false);
      }
    };

    if (docId) {
      loadExistingAnalysis();
    }
  }, [docId, fetchSimilarCars]);

  const startAnalysis = useCallback(async (sendEmail: boolean) => {
    if (!docId) {
      setError('docId is not available.');
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysisStarted(true);
    setAnalysisSteps(prev => {
      const newSteps = { ...prev };
      stepDefinitions.forEach(step => {
        newSteps[step.key] = { status: 'in_progress' };
      });
      return newSteps;
    });

    try {
      const currentUser = getFirebaseAuth().currentUser;
      if (!currentUser) throw new Error('No authenticated user found');
      const token = await currentUser.getIdToken();

      // Fetch similar cars in parallel with the analysis stream
      fetchSimilarCars();

      const response = await fetch(`${PPI_API}/api/create-ppi-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ doc_id: docId, sendEmail }),
      });

      if (!response.body) throw new Error('Response body is null');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      const processStream = async () => {
        let buffer = '';

        const processMessage = (message: string) => {
          if (message.startsWith('data:')) {
            try {
              const data = JSON.parse(message.substring(5));
              const { step, status, data: stepData, error, final, email, message: msg } = data;

              if (step && status) {
                setAnalysisSteps(prev => ({
                  ...prev,
                  [step]: { ...prev[step], status, data: stepData, error },
                }));
              }

              if (final) {
                setIsComplete(true);
                setLoading(false);
                setSentEmailAddress(email || null);
                if (status === 'error') {
                  setError(error || 'Analysis failed.');
                } else if (status === 'partial') {
                  setEmailError(msg || 'Email send failed.');
                }
              }
            } catch (e) {
              console.error('Failed to parse JSON:', message);
            }
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            // End of stream. Process any remaining data in the buffer.
            if (buffer) {
              processMessage(buffer);
            }
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const messages = buffer.split('\n\n');

          // The last message might be incomplete, so we keep it in the buffer.
          buffer = messages.pop() || '';

          messages.forEach(processMessage);
        }
      };

      await processStream();

    } catch (err) {
      setError('An error occurred with the analysis stream.');
      setLoading(false);
    }
  }, [docId, fetchSimilarCars]);

  const value = {
    analysisStarted,
    loading,
    error,
    analysisSteps,
    isComplete,
    sentEmailAddress,
    emailError,
    similarCars,
    startAnalysis,
    resetState,
  };

  return (
    <PpiAnalysisContext.Provider value={value}>
      {children}
    </PpiAnalysisContext.Provider>
  );
};